import { Injectable, inject } from '@angular/core';
import { AwsApiWrapperService } from '@brighthub/api';
import {
  ALL_STATUSES,
  Activity,
  ActivityStatus,
  CursorPage,
  ReviewAction,
} from '../models/activity.model';

/** Registered api-internal endpoint (see `environment.apiEndpoints`) that
 * fronts ai-activity-inbox-api. */
const API_NAME = 'api-mobile';
const BASE_PATH = '/api/v1/activities';

/** The backend caps `limit` at 100 per request. The feed asks for the maximum
 * so one call covers as much of a client's history as it allows. */
const PAGE_LIMIT = 100;

export interface ActivityFeed {
  /** Every activity retrieved for the client, newest first. */
  activities: Activity[];
  countsByStatus: Record<ActivityStatus, number>;
  /** True when the client filled a whole page, which means the counts are a
   * floor rather than totals. */
  truncated: boolean;
}

/** One entry of FastAPI's 422 `detail` array. */
interface FastApiValidationError {
  msg?: string;
  loc?: (string | number)[];
}

interface HttpErrorLike {
  status?: number;
  error?: { detail?: string | FastApiValidationError[] };
  message?: string;
}

/**
 * Turn a rethrown `HttpErrorResponse` into something worth showing a user.
 *
 * FastAPI sends `detail` as a string for a raised `HTTPException` but as an
 * array of per-field errors for a 422, so the array has to be flattened or the
 * message renders as "[object Object]" and hides the only real diagnostic.
 */
export function describeHttpError(err: unknown): string {
  const httpErr = err as HttpErrorLike;
  const raw = httpErr?.error?.detail;
  const detail = Array.isArray(raw)
    ? raw
        .map((entry) =>
          [entry.loc?.join('.'), entry.msg].filter(Boolean).join(': '),
        )
        .join('; ')
    : raw;
  const status = httpErr?.status;
  if (detail && status) return `${status} ${detail}`;
  return detail ?? httpErr?.message ?? 'Unknown error';
}

/**
 * Read/write access to ai-activity-inbox-api through api-internal.
 *
 * Deliberately calls {@link AwsApiWrapperService} rather than the higher-level
 * `ApiService`: `ApiService` assumes every backend wraps its response as
 * `{ result: { status, code, ... } }` (the lambda-backed api-mobile
 * convention), and ai-activity-inbox-api is a plain FastAPI service returning
 * raw JSON, so `ApiService` throws inside its own pipeline even on a 200.
 * `AwsApiWrapperService` still injects the Okta bearer token and standard
 * headers, it just doesn't assume a response shape.
 *
 * Stateless by design — callers own the signals so a dialog and a list can hold
 * independent views of the same activity.
 */
@Injectable({ providedIn: 'root' })
export class ActivityInboxService {
  private readonly _api = inject(AwsApiWrapperService);

  /**
   * Every activity for one client, across all statuses, for the Review tab on
   * that client's page.
   *
   * One request rather than one per status: `client_guid` reads the
   * `by_client` GSI, which has no status in its key, so a single query already
   * spans every status and the counts fall out of bucketing it here. The
   * backend rejects `status` alongside `client_guid` for that reason.
   */
  async loadClientFeed(clientGuid: string): Promise<ActivityFeed> {
    const page = await this._request<CursorPage<Activity>>(() =>
      this._api.get(API_NAME, BASE_PATH, {
        queryParams: { client_guid: clientGuid, limit: String(PAGE_LIMIT) },
      }),
    );

    const countsByStatus = {} as Record<ActivityStatus, number>;
    for (const status of ALL_STATUSES) countsByStatus[status] = 0;
    for (const activity of page.items) countsByStatus[activity.status] += 1;

    return {
      activities: [...page.items].sort(
        (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at),
      ),
      countsByStatus,
      truncated: page.has_more,
    };
  }

  async getActivity(activityId: string): Promise<Activity> {
    return this._request<Activity>(() =>
      this._api.get(API_NAME, `${BASE_PATH}/${activityId}`),
    );
  }

  /** Appends a note without transitioning the activity. The backend treats PUT
   * and PATCH identically today — `note` is the only editable field — so this
   * uses PATCH. No UI calls this since the standalone "Save note" button came
   * out of the dialog; notes now ride along with confirm / retry / deny. */
  async addNote(activityId: string, note: string): Promise<Activity> {
    return this._request<Activity>(() =>
      this._api.patch(API_NAME, `${BASE_PATH}/${activityId}`, {
        body: { note },
      }),
    );
  }

  /**
   * Confirm, retry, or deny. `confirm` returns 501 until an execution agent
   * exists; `retry` re-invokes the router agent with the note as feedback, and
   * `deny` is a terminal status change.
   */
  async applyAction(
    activityId: string,
    action: ReviewAction,
    note?: string,
  ): Promise<Activity> {
    return this._request<Activity>(() =>
      this._api.post(API_NAME, `${BASE_PATH}/${activityId}/actions`, {
        body: { action, note },
      }),
    );
  }

  /** `AwsApiWrapperService` resolves to `false` instead of throwing when the
   * Okta token isn't available yet, which would otherwise surface as a
   * confusing "cannot read property of false" further up. */
  private async _request<T>(send: () => Promise<unknown>): Promise<T> {
    const response = await send();
    if (response === false) {
      throw new Error(
        'Not signed in yet — no access token available for the request.',
      );
    }
    return response as T;
  }
}
